import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import { DBContext } from '../DBContext';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { MdWhatshot, MdAcUnit, MdTrendingUp, MdTrendingDown } from 'react-icons/md';

const BASE = process.env.REACT_APP_API_BASE_URL;

const PERIOD_OPTIONS = [
    { value: 'today', label: 'Today' },
    { value: 'week', label: 'This Week' },
    { value: 'month', label: 'This Month' },
];

const FIRE_COLORS = {
    today: '#ff6b00',   // orange fire — today's top
    week: '#6366f1',    // indigo fire — week's top
    month: '#0ea5e9',   // blue fire — month's top
};

export default function TrendsPage() {
    const { logginedDB } = useContext(DBContext);
    const db = logginedDB;

    const [period, setPeriod] = useState('today');
    const [data, setData] = useState({ all: [], top5: [], least5: [] });
    const [loading, setLoading] = useState(true);

    const fetchTrends = useCallback(async () => {
        if (!db) return;
        try {
            setLoading(true);
            const res = await axios.get(`${BASE}/production/trends/${db}?period=${period}`);
            setData(res.data || { all: [], top5: [], least5: [] });
        } catch (err) {
            console.error(err);
        } finally { setLoading(false); }
    }, [db, period]);

    useEffect(() => { fetchTrends(); }, [fetchTrends]);

    const fireColor = FIRE_COLORS[period];
    const topItem = data.top5[0];

    return (
        <div className="inventory-page" style={{ height: '100%', display: 'flex', flexDirection: 'column', gap: '.75rem', overflow: 'hidden' }}>

            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '.5rem' }}>
                <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: '#2b3034', display: 'flex', alignItems: 'center', gap: '.4rem' }}>
                    <MdTrendingUp color="#0008b4" /> Sales Trends
                </h2>
                <div style={{ display: 'flex', gap: '.4rem', background: '#f3f4f6', borderRadius: '.375rem', padding: '.2rem' }}>
                    {PERIOD_OPTIONS.map(opt => (
                        <button key={opt.value} onClick={() => setPeriod(opt.value)}
                            style={{ border: 'none', cursor: 'pointer', padding: '.35rem .85rem', borderRadius: '.25rem', fontSize: '.85rem', fontWeight: period === opt.value ? 700 : 400, background: period === opt.value ? '#0008b4' : 'transparent', color: period === opt.value ? '#fff' : '#374151', transition: '.2s' }}>
                            {opt.label}
                        </button>
                    ))}
                </div>
            </div>

            {loading ? (
                <div className="inventory-empty">Loading trends...</div>
            ) : data.all.length === 0 ? (
                <div className="inventory-empty">
                    <MdTrendingUp size={40} color="#dadada" />
                    <p>No sales data found for this period.</p>
                </div>
            ) : (
                <div style={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: 'auto 1fr', gap: '.75rem', overflow: 'auto' }}>

                    {/* 🔥 Top Sellers */}
                    <div style={{ background: '#fff', border: '1px solid #dadada', borderRadius: '.313rem', overflow: 'hidden' }}>
                        <div style={{ padding: '.75rem 1rem', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: '.4rem', fontWeight: 700 }}>
                            <MdWhatshot color={fireColor} size={20} />
                            Top Sellers
                            <span style={{ fontSize: '.78rem', color: '#9ca3af', fontWeight: 400, marginLeft: 'auto' }}>
                                {period === 'today' ? 'Today' : period === 'week' ? 'Last 7 days' : 'Last 30 days'}
                            </span>
                        </div>
                        <div style={{ padding: '.5rem 0' }}>
                            {data.top5.map((item, i) => (
                                <div key={item.item_id} style={{ display: 'flex', alignItems: 'center', gap: '.75rem', padding: '.5rem 1rem', borderBottom: i < data.top5.length - 1 ? '1px solid #f9f9f9' : 'none' }}>
                                    <div style={{ width: '1.75rem', height: '1.75rem', borderRadius: '50%', background: i === 0 ? fireColor : '#f3f4f6', color: i === 0 ? '#fff' : '#374151', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '.85rem', flexShrink: 0 }}>
                                        {i === 0 ? <MdWhatshot color="#fff" size={16} /> : i + 1}
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontWeight: 600, color: '#2b3034', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.item_name}</div>
                                        <div style={{ fontSize: '.78rem', color: '#6b7280' }}>{item.kot_count} KOTs</div>
                                    </div>
                                    <div style={{ fontWeight: 700, color: fireColor, fontSize: '1rem' }}>{item.total_qty}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* ❄️ Least Sold */}
                    <div style={{ background: '#fff', border: '1px solid #dadada', borderRadius: '.313rem', overflow: 'hidden' }}>
                        <div style={{ padding: '.75rem 1rem', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: '.4rem', fontWeight: 700 }}>
                            <MdAcUnit color="#60a5fa" size={20} />
                            Least Sold
                        </div>
                        <div style={{ padding: '.5rem 0' }}>
                            {data.least5.map((item, i) => (
                                <div key={item.item_id} style={{ display: 'flex', alignItems: 'center', gap: '.75rem', padding: '.5rem 1rem', borderBottom: i < data.least5.length - 1 ? '1px solid #f9f9f9' : 'none' }}>
                                    <div style={{ width: '1.75rem', height: '1.75rem', borderRadius: '50%', background: '#eff6ff', color: '#60a5fa', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '.85rem', flexShrink: 0 }}>
                                        <MdAcUnit size={14} />
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontWeight: 600, color: '#2b3034', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.item_name}</div>
                                        <div style={{ fontSize: '.78rem', color: '#6b7280' }}>{item.kot_count} KOTs</div>
                                    </div>
                                    <div style={{ fontWeight: 700, color: '#60a5fa', fontSize: '1rem' }}>{item.total_qty}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Bar Chart — All Items */}
                    <div style={{ gridColumn: 'span 2', background: '#fff', border: '1px solid #dadada', borderRadius: '.313rem', overflow: 'hidden', minHeight: '16rem' }}>
                        <div style={{ padding: '.75rem 1rem', borderBottom: '1px solid #f0f0f0', fontWeight: 700 }}>All Items — Quantity Sold</div>
                        <div style={{ padding: '1rem', height: '14rem' }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={data.all.slice(0, 20)} margin={{ top: 0, right: 0, left: -10, bottom: 40 }}>
                                    <XAxis dataKey="item_name" tick={{ fontSize: 11 }} angle={-35} textAnchor="end" interval={0} />
                                    <YAxis tick={{ fontSize: 11 }} />
                                    <Tooltip formatter={(v) => [v, 'Qty Sold']} />
                                    <Bar dataKey="total_qty" radius={[4, 4, 0, 0]}>
                                        {data.all.slice(0, 20).map((entry, index) => (
                                            <Cell key={index} fill={
                                                data.top5.some(t => t.item_id === entry.item_id) ? fireColor :
                                                    data.least5.some(t => t.item_id === entry.item_id) ? '#bfdbfe' : '#c7d2fe'
                                            } />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
